import React, { useState, useEffect, useRef } from 'react';
import { MarketRealm, MarketRealmSwitcher } from './common/MarketRealmSwitcher';

export interface TopHeaderProps {
  activeView: string;
  currentRealm?: MarketRealm;
  onSelectRealm?: (realm: MarketRealm) => void;
  colorMode?: 'mainland' | 'international';
  onToggleColorMode?: () => void;
  alertCount?: number;
  hasCriticalAlert?: boolean;
  onOpenAlerts?: () => void;
  onToggleMobileMenu?: () => void;
  theme?: 'daylight' | 'dark';
  onToggleTheme?: () => void;
  cpuPercent?: number | null;
  ramPercent?: number | null;
  diskPercent?: number | null;
  uptimeText?: string;
  freshnessText?: string;
  freshnessColor?: string;
  ashareSessionText?: string;
  sysLoadText?: string;
  sysLoadColor?: string;
}


function getHardwareColor(percent: number | null | undefined): string {
  if (percent === null || percent === undefined) return 'var(--text-muted)';
  if (percent < 70) return 'var(--color-emerald)';
  if (percent < 85) return 'var(--color-warning)';
  return 'var(--color-bearish)';
}

export const TopHeader: React.FC<TopHeaderProps> = ({
  activeView: _activeView,
  currentRealm = 'crypto',
  onSelectRealm,
  colorMode = 'mainland',
  onToggleColorMode,
  alertCount = 0,
  hasCriticalAlert = false,
  onOpenAlerts,
  onToggleMobileMenu,
  theme = 'daylight',
  onToggleTheme,
  cpuPercent,
  ramPercent,
  diskPercent,
  uptimeText,
  freshnessText: _freshnessText,
  freshnessColor: _freshnessColor,
  ashareSessionText: _ashareSessionText,
  sysLoadText,
  sysLoadColor: _sysLoadColor,
}) => {
  const [isAuthPopoverOpen, setIsAuthPopoverOpen] = useState<boolean>(false);
  const authPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (authPopoverRef.current && !authPopoverRef.current.contains(e.target as Node)) {
        setIsAuthPopoverOpen(false);
      }
    };
    if (isAuthPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAuthPopoverOpen]);

  return (
    <header className="top-header" role="banner">
      <div className="header-left">
        <button
          className="mobile-menu-btn"
          id="mobile-menu-btn"
          onClick={onToggleMobileMenu}
          aria-label="打开侧边导航栏"
          title="打开导航菜单"
        >
          <span>☰</span>
        </button>
        {onSelectRealm && (
          <MarketRealmSwitcher currentRealm={currentRealm} onSelectRealm={onSelectRealm} />
        )}
        {currentRealm === 'ashare' && onToggleColorMode && (
          <button
            id="hdr-btn-toggle-colormode"
            onClick={onToggleColorMode}
            style={{
              padding: '3px 8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 500,
              border: '1px solid var(--border-subtle)',
              background: 'var(--bg-card)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
            title="切换 A 股涨跌配色偏好"
          >
            {colorMode === 'mainland' ? '🇨🇳 红涨绿跌' : '🌐 绿涨红跌'}
          </button>
        )}
      </div>

      <div className="header-meta">
        {/* Hardware Infrastructure Monitoring Cluster */}
        <div className="meta-item" title="CPU 当前硬件负载率">
          <span className="meta-label">CPU 负载</span>
          <span
            className="meta-val"
            id="hdr-cpu"
            style={{ color: getHardwareColor(cpuPercent) }}
          >
            {cpuPercent !== null && cpuPercent !== undefined ? `${Math.round(cpuPercent)}%` : (sysLoadText || '--')}
          </span>
        </div>

        <div className="meta-item" title="宿主机与容器内存占用率">
          <span className="meta-label">内存 RAM</span>
          <span
            className="meta-val"
            id="hdr-ram"
            style={{ color: getHardwareColor(ramPercent) }}
          >
            {ramPercent !== null && ramPercent !== undefined ? `${Math.round(ramPercent)}%` : '--'}
          </span>
        </div>

        <div className="meta-item" title="NVMe 数据盘使用率">
          <span className="meta-label">磁盘 SSD</span>
          <span
            className="meta-val"
            id="hdr-ssd"
            style={{ color: getHardwareColor(diskPercent) }}
          >
            {diskPercent !== null && diskPercent !== undefined ? `${Math.round(diskPercent)}%` : '--'}
          </span>
        </div>

        <div className="meta-item" title="服务连续运行时长 (Uptime)">
          <span className="meta-label">系统运行</span>
          <span className="meta-val mono" id="hdr-uptime" style={{ color: 'var(--accent-cyan)' }}>
            {uptimeText || 'UP --'}
          </span>
        </div>

        <div
          className="meta-item"
          style={{ cursor: onOpenAlerts ? 'pointer' : 'default' }}
          onClick={onOpenAlerts}
          title="点击查看系统警报"
        >
          <span className="meta-label">异常警报</span>
          <span
            className="meta-val"
            id="hdr-alerts"
            style={{
              color:
                alertCount > 0
                  ? hasCriticalAlert
                    ? 'var(--color-bearish)'
                    : 'var(--color-warning)'
                  : 'var(--color-emerald)',
            }}
          >
            🛡️ {alertCount} 警报
          </span>
        </div>



        <button
          className="theme-toggle-btn"
          id="theme-toggle-btn"
          data-testid="theme-toggle"
          onClick={onToggleTheme}
          title={`切换主题 (快捷键 T) · 当前: ${theme === 'dark' ? '深空暗黑' : '白昼鼠尾草'}`}
          aria-label="切换主题"
          style={{
            padding: '4px 8px',
            minWidth: '32px',
            height: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          <span className="theme-toggle-icon">{theme === 'dark' ? '🌙' : '☀️'}</span>
        </button>

        <div ref={authPopoverRef} style={{ position: 'relative', display: 'inline-flex' }}>
          <button
            className="btn-auth"
            id="btn-open-auth"
            onClick={() => setIsAuthPopoverOpen((prev) => !prev)}
            title="同源代理凭据: 已就绪 ✓ (点击查看网关授权详情)"
            aria-label="同源代理授权详情"
            style={{
              padding: '4px 8px',
              minWidth: '32px',
              height: '28px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              fontSize: '13px',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <span>🔑</span>
            <span
              style={{
                position: 'absolute',
                top: '3px',
                right: '3px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--color-emerald)',
                boxShadow: '0 0 4px var(--color-emerald)',
              }}
            />
          </button>

          {isAuthPopoverOpen && (
            <div
              className="auth-dropdown-popover"
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                right: 0,
                width: '260px',
                background: 'var(--bg-surface-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '8px',
                boxShadow: '0 8px 24px var(--border-subtle)',
                padding: '12px 14px',
                zIndex: 1000,
                fontSize: '12px',
                lineHeight: 1.5,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🔑</span> Ingress BFF 同源代理
                </span>
                <span style={{ color: 'var(--color-emerald)', fontSize: '11px', fontWeight: 600 }}>
                  ● 正常就绪
                </span>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div>• <strong>安全模式:</strong> 只读保护 (GATE-NEG-05)</div>
                <div>• <strong>网关鉴权:</strong> 生产同源凭据自动注入</div>
                <div>• <strong>状态探针:</strong> 200 OK (<span id="auth-status-text">同源代理已就绪 ✓</span>)</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
